import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDebounce } from '../../shared/hooks/useDebounce';
import { X, MapPin, DollarSign, Briefcase, Clock, ChevronLeft, ChevronRight, Check, Bookmark, LayoutGrid, List } from 'lucide-react';
import CandidateLayout from '../components/CandidateLayout';
import { jobApiService, Job } from '../../recruiter/services/jobApi';
import { applicationApiService } from '../services/applicationApi';
import { savedJobsApi } from '../services/savedJobsApi';
import { useAuthProtection } from '../hooks/useAuthProtection';
import Modal from '../../shared/components/modern/Modal';

type FilterType = 'all' | 'applied' | 'saved';

/**
 * Jobs Page
 * Displays all available jobs from MongoDB with apply functionality
 * Protected Route: Only accessible to authenticated candidates
 */
const Jobs: React.FC = () => {
  // Protect route - redirect to login if not authenticated
  useAuthProtection();

  const navigate = useNavigate();
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [userId, setUserId] = useState('');
  const [jobs, setJobs] = useState<Job[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [appliedJobs, setAppliedJobs] = useState<Set<string>>(new Set());
  const [savedJobs, setSavedJobs] = useState<Set<string>>(new Set());
  const [applyingJobId, setApplyingJobId] = useState<string | null>(null);
  const [savingJobId, setSavingJobId] = useState<string | null>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalJobs, setTotalJobs] = useState(0);
  const itemsPerPage = 12;

  // View mode: grid or list
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  // Filter state
  const [activeFilter, setActiveFilter] = useState<FilterType>('all');

  // Search state with URL sync
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchInput, setSearchInput] = useState(searchParams.get('q') || '');
  const debouncedSearch = useDebounce(searchInput, 300);

  // Filters
  const [selectedExperience, setSelectedExperience] = useState<string | null>(null);
  const [companyFilter, setCompanyFilter] = useState('');
  const debouncedCompany = useDebounce(companyFilter, 300);
  const [skillFilter, setSkillFilter] = useState('');
  const debouncedSkill = useDebounce(skillFilter, 300);

  // Modal states
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [isJobDetailsOpen, setIsJobDetailsOpen] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successJobTitle, setSuccessJobTitle] = useState('');
  const [isApplicationError, setIsApplicationError] = useState(false);

  // Consent modal states
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [consentJob, setConsentJob] = useState<Job | null>(null);
  const [consentChecked, setConsentChecked] = useState(false);

  // Get user data from localStorage
  useEffect(() => {
    const userDataString = localStorage.getItem('authUser');
    if (userDataString) {
      try {
        const userData = JSON.parse(userDataString);
        const name = userData.firstName && userData.lastName
          ? `${userData.firstName} ${userData.lastName}`
          : userData.email?.split('@')[0] || 'User';

        setUserName(name);
        setUserEmail(userData.email || '');
        setUserId(userData.id || '');
      } catch (error) {
        console.error('Failed to parse user data:', error);
      }
    }
  }, []);

  // Helper to get job ID (handles both 'id' and '_id' fields)
  const getJobId = (job: Job): string => {
    return job.id || job._id || '';
  };

  // Function to fetch applications
  const fetchApplications = async (): Promise<Set<string>> => {
    try {
      const response = await applicationApiService.getMyApplications();
      // Handle both response formats from backend
      const applicationsData = response.payload?.data || response.data || [];
      console.log('Fetched applications:', applicationsData);
      const jobIds = new Set(applicationsData.map(app => app.jobId));
      console.log('Applied job IDs:', Array.from(jobIds));
      return jobIds;
    } catch (appError) {
      console.error('Failed to fetch applications:', appError);
      return new Set();
    }
  };

  // Function to fetch saved jobs
  const fetchSavedJobs = async (): Promise<Set<string>> => {
    try {
      const response = await savedJobsApi.getSavedJobs();
      const savedJobsData = response.payload?.data || [];
      console.log('Fetched saved jobs:', savedJobsData);
      const jobIds = new Set(savedJobsData.map((job: Job) => job.id || job._id || ''));
      console.log('Saved job IDs:', Array.from(jobIds));
      return jobIds;
    } catch (error) {
      console.error('Failed to fetch saved jobs:', error);
      return new Set();
    }
  };

  // 7j: Fetch jobs from API with active filters applied server-side
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);

        // Pass active filters to API (server-side filtering)
        const apiParams: {
          page?: number; limit?: number; q?: string;
          experienceLevel?: string; company?: string; skills?: string;
        } = { page: currentPage, limit: itemsPerPage };

        if (debouncedSearch) apiParams.q = debouncedSearch;
        if (selectedExperience) apiParams.experienceLevel = selectedExperience;
        if (debouncedCompany) apiParams.company = debouncedCompany;
        if (debouncedSkill) apiParams.skills = debouncedSkill;

        const [jobsResult, appliedJobIds, savedJobIds] = await Promise.all([
          jobApiService.getAllJobs(apiParams),
          fetchApplications(),
          fetchSavedJobs(),
        ]);

        const { jobs: fetchedJobs, totalItems, totalPages: tp } = jobsResult;
        console.log('Fetched jobs:', fetchedJobs.map(j => ({ id: j.id, _id: j._id, title: j.title })));

        setAppliedJobs(appliedJobIds);
        setSavedJobs(savedJobIds);
        setJobs(fetchedJobs);
        setTotalJobs(totalItems);
        setTotalPages(tp);
      } catch (error) {
        console.error('Error fetching jobs:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  // Re-fetch when any filter or page changes
  }, [currentPage, debouncedSearch, selectedExperience, debouncedCompany, debouncedSkill]);

  // Sync debounced search to URL ?q= param
  useEffect(() => {
    const newParams = new URLSearchParams(searchParams);
    if (debouncedSearch) {
      newParams.set('q', debouncedSearch);
    } else {
      newParams.delete('q');
    }
    setSearchParams(newParams, { replace: true });
  }, [debouncedSearch]);

  // Reset pagination when any filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, selectedExperience, debouncedCompany, debouncedSkill]);

  // Normalize inconsistent experience level strings from backend
  const normalizeExperience = (raw: string): string => {
    const lower = raw.toLowerCase().trim();
    if (lower === 'entry' || lower === 'entry level' || lower === 'entry-level') return 'Entry';
    if (lower.includes('entry') && lower.includes('mid')) return 'Entry to Mid';
    if (lower === 'mid' || lower === 'mid-level' || lower === 'mid level') return 'Mid-Level';
    if (lower === 'senior' || lower === 'senior level' || lower === 'senior-level') return 'Senior';
    if (lower === 'principal') return 'Principal';
    if (lower === 'lead') return 'Lead';
    if (lower === 'executive' || lower === 'c-level') return 'Executive';
    return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
  };

  // Extract unique normalized experience levels from jobs
  const experienceLevels = React.useMemo(() => {
    const levels = new Set<string>();
    jobs.forEach(job => {
      if (job.experienceLevel) levels.add(normalizeExperience(job.experienceLevel));
    });
    return Array.from(levels).sort();
  }, [jobs]);

  // 7j: Filters are now server-side (search, experience, company, skill).
  // Tab filter (All / Applied / Saved) is still applied client-side on the server-returned page.
  const getFilteredJobs = () => {
    if (activeFilter === 'applied') return jobs.filter(job => appliedJobs.has(getJobId(job)));
    if (activeFilter === 'saved') return jobs.filter(job => savedJobs.has(getJobId(job)));
    return jobs; // 'all' — server already filters out nothing special
  };

  // Pagination is server-side; just return the already-paginated jobs array
  const getPaginatedJobs = () => getFilteredJobs();

  const getFilteredTotalPages = () => totalPages;

  // Show consent modal before applying
  const handleApply = (job: Job, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }

    if (!userId) {
      navigate('/auth/login');
      return;
    }

    const jobId = job.id || job._id;
    if (!jobId) {
      return;
    }

    // Show consent modal
    setConsentJob(job);
    setConsentChecked(false);
    setShowConsentModal(true);
  };

  // Submit application after consent is given
  const submitApplication = async () => {
    if (!consentJob || !consentChecked) return;

    const jobId = consentJob.id || consentJob._id;
    if (!jobId) return;

    try {
      setApplyingJobId(jobId);
      setShowConsentModal(false);

      await applicationApiService.submitApplication({
        jobId: jobId,
        recruiterId: consentJob.postedBy || consentJob.recruiterId || '',
        profileShareConsent: true,
      });

      // Update applied jobs set immediately
      setAppliedJobs(prev => new Set(prev).add(jobId));

      // Show success modal
      setSuccessJobTitle(consentJob.title || 'the position');
      setIsApplicationError(false);
      setShowSuccessModal(true);

      // Close job details modal if open
      setIsJobDetailsOpen(false);
    } catch (error: any) {
      console.error('Error applying to job:', error);
      // Show error in a modal
      setSuccessJobTitle(error.message || 'Failed to submit application. Please try again.');
      setIsApplicationError(true);
      setShowSuccessModal(true);
    } finally {
      setApplyingJobId(null);
      setConsentJob(null);
    }
  };

  const handleJobCardClick = (job: Job) => {
    setSelectedJob(job);
    setIsJobDetailsOpen(true);
  };

  // Handle save/unsave job
  const handleSaveJob = async (job: Job, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }

    if (!userId) {
      navigate('/auth/login');
      return;
    }

    const jobId = getJobId(job);
    if (!jobId) return;

    const isSaved = savedJobs.has(jobId);
    setSavingJobId(jobId);

    try {
      if (isSaved) {
        await savedJobsApi.unsaveJob(jobId);
        setSavedJobs(prev => {
          const next = new Set(prev);
          next.delete(jobId);
          return next;
        });
      } else {
        await savedJobsApi.saveJob(jobId);
        setSavedJobs(prev => new Set(prev).add(jobId));
      }
    } catch (error) {
      console.error('Failed to save/unsave job:', error);
    } finally {
      setSavingJobId(null);
    }
  };

  const formatSalary = (salary: string | undefined) => {
    if (!salary) return 'Not specified';
    return salary;
  };

  const formatLocation = (location: any) => {
    if (!location) return 'Not specified';
    if (typeof location === 'string') return location;
    if (typeof location === 'object') {
      const { city, state, country } = location;
      return [city, state, country].filter(Boolean).join(', ');
    }
    return 'Not specified';
  };

  const handleFilterChange = (filter: FilterType) => {
    setActiveFilter(filter);
    setCurrentPage(1);
  };

  const renderPagination = () => {
    const totalPagesFiltered = getFilteredTotalPages();
    if (totalPagesFiltered <= 1) return null;

    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPagesFiltered, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // Previous button
    pages.push(
      <button
        key="prev"
        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
        disabled={currentPage === 1}
        className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <ChevronLeft size={16} />
      </button>
    );

    // First page
    if (startPage > 1) {
      pages.push(
        <button
          key={1}
          onClick={() => setCurrentPage(1)}
          className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100"
        >
          1
        </button>
      );
      if (startPage > 2) {
        pages.push(<span key="ellipsis1" className="px-2">...</span>);
      }
    }

    // Page numbers
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => setCurrentPage(i)}
          className={`w-8 h-8 flex items-center justify-center rounded-lg font-medium ${
            currentPage === i
              ? 'bg-[#015FA6] text-white'
              : 'hover:bg-gray-100'
          }`}
        >
          {i}
        </button>
      );
    }

    // Last page
    if (endPage < totalPagesFiltered) {
      if (endPage < totalPagesFiltered - 1) {
        pages.push(<span key="ellipsis2" className="px-2">...</span>);
      }
      pages.push(
        <button
          key={totalPagesFiltered}
          onClick={() => setCurrentPage(totalPagesFiltered)}
          className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100"
        >
          {totalPagesFiltered}
        </button>
      );
    }

    // Next button
    pages.push(
      <button
        key="next"
        onClick={() => setCurrentPage(Math.min(totalPagesFiltered, currentPage + 1))}
        disabled={currentPage === totalPagesFiltered}
        className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <ChevronRight size={16} />
      </button>
    );

    return (
      <div className="flex justify-center items-center gap-2 mt-6">
        {pages}
      </div>
    );
  };

  const paginatedJobs = getPaginatedJobs();
  const filteredJobsCount = getFilteredJobs().length;
  const rangeStart = filteredJobsCount === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const rangeEnd = rangeStart === 0 ? 0 : rangeStart + paginatedJobs.length - 1;

  return (
    <CandidateLayout
      headerTitle="Browse Jobs"
      headerSubtitle="Discover and apply to jobs that match your skills and interests"
      searchConfig={{
        placeholder: "Search by title, company, location...",
        value: searchInput,
        onChange: setSearchInput,
      }}
    >
      {/* Combined Filter + Jobs Container */}
      <div className="bg-white rounded-2xl shadow-sm border border-[#EDEDED]">

        {/* Filter Bar */}
        <div className="p-4 border-b border-[#EDEDED]">
        {/* Top row: tabs + filter toggle + count */}
        <div className="flex items-center justify-between">
          <div className="flex gap-2 items-center">
            <button
              onClick={() => handleFilterChange('all')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeFilter === 'all'
                  ? 'bg-[#015FA6] text-white'
                  : 'bg-[#F1F3F4] text-[#515151] hover:bg-[#E3F2FD]'
              }`}
            >
              All Jobs ({jobs.filter(j => !appliedJobs.has(getJobId(j))).length})
            </button>
            <button
              onClick={() => handleFilterChange('saved')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeFilter === 'saved'
                  ? 'bg-[#015FA6] text-white'
                  : 'bg-[#F1F3F4] text-[#515151] hover:bg-[#E3F2FD]'
              }`}
            >
              Saved Jobs ({savedJobs.size})
            </button>
            <button
              onClick={() => handleFilterChange('applied')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeFilter === 'applied'
                  ? 'bg-[#015FA6] text-white'
                  : 'bg-[#F1F3F4] text-[#515151] hover:bg-[#E3F2FD]'
              }`}
            >
              Applied Jobs ({jobs.filter(j => appliedJobs.has(getJobId(j))).length})
            </button>

          </div>

          <div className="flex items-center gap-3">
            <p className="text-sm text-[#757575]">
              {debouncedSearch
                ? `${filteredJobsCount} result${filteredJobsCount !== 1 ? 's' : ''} for "${debouncedSearch}"`
                : filteredJobsCount === 0
                ? 'No jobs found'
                : `Showing ${rangeStart}–${rangeEnd} of ${filteredJobsCount} jobs`}
            </p>
            {/* View toggle */}
            <div className="flex items-center bg-[#F1F3F4] rounded-lg p-1 gap-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-1.5 rounded-md transition-colors ${viewMode === 'grid' ? 'bg-white text-[#015FA6] shadow-sm' : 'text-[#757575] hover:text-[#015FA6]'}`}
                title="Grid view"
              >
                <LayoutGrid size={16} />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-1.5 rounded-md transition-colors ${viewMode === 'list' ? 'bg-white text-[#015FA6] shadow-sm' : 'text-[#757575] hover:text-[#015FA6]'}`}
                title="List view"
              >
                <List size={16} />
              </button>
            </div>
          </div>
        </div>

        {/* Filter inputs row */}
        <div className="flex gap-3 mt-3 pt-3 border-t border-gray-100">
          {/* Experience dropdown */}
          <select
            value={selectedExperience || ''}
            onChange={(e) => setSelectedExperience(e.target.value || null)}
            className="h-9 px-3 rounded-lg border border-gray-200 bg-white text-sm font-medium text-[#515151] focus:outline-none focus:border-[#015FA6] focus:ring-2 focus:ring-[#015FA6]/10 transition-all cursor-pointer"
          >
            <option value="">All Experience</option>
            {experienceLevels.map(level => (
              <option key={level} value={level}>{level}</option>
            ))}
          </select>

          {/* Company text input */}
          <div className="relative">
            <input
              type="text"
              placeholder="Filter by company..."
              value={companyFilter}
              onChange={(e) => setCompanyFilter(e.target.value)}
              className="h-9 w-[180px] pl-3 pr-7 rounded-lg border border-gray-200 bg-white text-sm font-medium text-[#515151] focus:outline-none focus:border-[#015FA6] focus:ring-2 focus:ring-[#015FA6]/10 transition-all"
            />
            {companyFilter && (
              <button
                onClick={() => setCompanyFilter('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center rounded-full bg-gray-200 hover:bg-gray-300 transition-colors"
              >
                <X size={10} className="text-gray-500" />
              </button>
            )}
          </div>

          {/* Skill text input */}
          <div className="relative">
            <input
              type="text"
              placeholder="Filter by skill..."
              value={skillFilter}
              onChange={(e) => setSkillFilter(e.target.value)}
              className="h-9 w-[180px] pl-3 pr-7 rounded-lg border border-gray-200 bg-white text-sm font-medium text-[#515151] focus:outline-none focus:border-[#015FA6] focus:ring-2 focus:ring-[#015FA6]/10 transition-all"
            />
            {skillFilter && (
              <button
                onClick={() => setSkillFilter('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center rounded-full bg-gray-200 hover:bg-gray-300 transition-colors"
              >
                <X size={10} className="text-gray-500" />
              </button>
            )}
          </div>

          {/* Clear all filters */}
          {(selectedExperience || companyFilter || skillFilter) && (
            <button
              onClick={() => { setSelectedExperience(null); setCompanyFilter(''); setSkillFilter(''); }}
              className="h-9 px-3 text-xs font-medium text-[#015FA6] hover:underline"
            >
              Clear filters
            </button>
          )}
        </div>
        </div>

        {/* Jobs List */}
        <div className="p-6">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="flex flex-col items-center gap-2">
              <div className="w-8 h-8 border-4 border-[#015FA6] border-t-transparent rounded-full animate-spin" />
              <p className="font-medium text-sm text-[#757575]">Loading jobs...</p>
            </div>
          </div>
        ) : paginatedJobs.length === 0 ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <svg className="w-16 h-16 mx-auto mb-4 text-[#A5A5A5]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <p className="text-lg font-medium text-[#0B111E]">
                {debouncedSearch
                  ? `No jobs matching "${debouncedSearch}"`
                  : activeFilter === 'applied' ? 'No applied jobs yet'
                  : activeFilter === 'saved' ? 'No saved jobs yet'
                  : 'No jobs available'}
              </p>
              <p className="text-sm text-[#757575] mt-1">
                {debouncedSearch
                  ? 'Try adjusting your search terms'
                  : activeFilter === 'applied' ? 'Start applying to see your applications here'
                  : activeFilter === 'saved' ? 'Click the bookmark icon on any job to save it for later'
                  : 'Check back later for new opportunities'}
              </p>
            </div>
          </div>
        ) : (
          <>
            {viewMode === 'grid' ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                {paginatedJobs.map((job) => {
                  const jobId = getJobId(job);
                  const hasApplied = appliedJobs.has(jobId);
                  const isSaved = savedJobs.has(jobId);
                  const isApplying = applyingJobId === jobId;
                  const isSaving = savingJobId === jobId;

                  return (
                    <div
                      key={jobId}
                      onClick={() => handleJobCardClick(job)}
                      className="border border-[#EDEDED] rounded-2xl p-5 shadow-sm hover:shadow-lg transition-all cursor-pointer hover:border-[#015FA6] flex flex-col"
                    >
                      {/* Job Header with Save Button */}
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex-1 min-w-0 pr-2">
                          <h3 className="text-base font-semibold text-[#0B111E] mb-0.5 truncate">{job.title}</h3>
                          <p className="text-sm text-[#515151]">{job.companyName}</p>
                        </div>
                        <button
                          onClick={(e) => handleSaveJob(job, e)}
                          disabled={isSaving}
                          className={`p-2 rounded-full transition-colors flex-shrink-0 ${isSaved ? 'text-[#015FA6] bg-[#E3F2FD]' : 'text-[#A5A5A5] hover:text-[#015FA6] hover:bg-[#F1F3F4]'}`}
                          title={isSaved ? 'Unsave job' : 'Save job'}
                        >
                          {isSaving ? <div className="w-4 h-4 border-2 border-[#015FA6] border-t-transparent rounded-full animate-spin" /> : <Bookmark size={16} fill={isSaved ? 'currentColor' : 'none'} />}
                        </button>
                      </div>

                      {/* Job Details */}
                      <div className="space-y-1.5 mb-3">
                        <div className="flex items-center gap-1.5 text-xs text-[#757575]"><MapPin size={13} />{formatLocation(job.location)}</div>
                        <div className="flex items-center gap-1.5 text-xs text-[#757575]"><DollarSign size={13} />{formatSalary(job.salaryRange)}</div>
                        {job.employmentType && <div className="flex items-center gap-1.5 text-xs text-[#757575]"><Briefcase size={13} />{job.employmentType}</div>}
                      </div>

                      {/* Skills */}
                      {job.requirements?.requiredSkills && job.requirements.requiredSkills.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mb-3">
                          {job.requirements.requiredSkills.slice(0, 3).map((skill, index) => (
                            <span key={index} className="px-2 py-0.5 bg-[#F1F3F4] text-xs text-[#0B111E] rounded-md">{skill}</span>
                          ))}
                          {job.requirements.requiredSkills.length > 3 && (
                            <span className="px-2 py-0.5 bg-[#F1F3F4] text-xs text-[#757575] rounded-md">+{job.requirements.requiredSkills.length - 3}</span>
                          )}
                        </div>
                      )}

                      {/* Apply Button */}
                      <button
                        onClick={(e) => hasApplied ? e.stopPropagation() : handleApply(job, e)}
                        disabled={isApplying}
                        className={`mt-auto w-full py-2.5 px-4 rounded-lg font-medium text-sm transition-colors flex items-center justify-center gap-2 ${
                          hasApplied ? 'bg-[#E8F5E9] text-[#2E7D32] cursor-default'
                          : isApplying ? 'bg-[#014580] text-white cursor-wait'
                          : 'bg-[#015FA6] text-white hover:bg-[#014580]'
                        }`}
                      >
                        {isApplying ? (<><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Applying...</>)
                          : hasApplied ? (<><Check size={15} />Applied</>)
                          : 'Apply Now'}
                      </button>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {paginatedJobs.map((job) => {
                  const jobId = getJobId(job);
                  const hasApplied = appliedJobs.has(jobId);
                  const isSaved = savedJobs.has(jobId);
                  const isApplying = applyingJobId === jobId;
                  const isSaving = savingJobId === jobId;

                  return (
                    <div
                      key={jobId}
                      onClick={() => handleJobCardClick(job)}
                      className="flex items-center gap-4 p-4 border border-[#EDEDED] rounded-2xl shadow-sm hover:shadow-md hover:border-[#015FA6] transition-all cursor-pointer"
                    >
                      {/* Company avatar */}
                      <div className="w-11 h-11 rounded-xl bg-[#015FA6] flex items-center justify-center text-white font-bold text-base flex-shrink-0">
                        {(job.companyName || 'C').charAt(0)}
                      </div>

                      {/* Title + company */}
                      <div className="min-w-0 w-48 flex-shrink-0">
                        <h3 className="text-sm font-semibold text-[#0B111E] truncate">{job.title}</h3>
                        <p className="text-xs text-[#515151] truncate">{job.companyName}</p>
                      </div>

                      {/* Meta */}
                      <div className="flex items-center gap-4 flex-1 min-w-0">
                        <span className="flex items-center gap-1 text-xs text-[#757575] whitespace-nowrap"><MapPin size={13} />{formatLocation(job.location)}</span>
                        <span className="flex items-center gap-1 text-xs text-[#757575] whitespace-nowrap"><DollarSign size={13} />{formatSalary(job.salaryRange)}</span>
                        {job.employmentType && <span className="flex items-center gap-1 text-xs text-[#757575] whitespace-nowrap"><Briefcase size={13} />{job.employmentType}</span>}
                      </div>

                      {/* Skills */}
                      {job.requirements?.requiredSkills && job.requirements.requiredSkills.length > 0 && (
                        <div className="hidden lg:flex items-center gap-1.5 flex-shrink-0">
                          {job.requirements.requiredSkills.slice(0, 3).map((skill, index) => (
                            <span key={index} className="px-2 py-0.5 bg-[#F1F3F4] text-xs text-[#0B111E] rounded-md whitespace-nowrap">{skill}</span>
                          ))}
                          {job.requirements.requiredSkills.length > 3 && (
                            <span className="px-2 py-0.5 bg-[#F1F3F4] text-xs text-[#757575] rounded-md">+{job.requirements.requiredSkills.length - 3}</span>
                          )}
                        </div>
                      )}

                      {/* Actions */}
                      <div className="flex items-center gap-2 flex-shrink-0 ml-auto">
                        <button
                          onClick={(e) => handleSaveJob(job, e)}
                          disabled={isSaving}
                          className={`p-2 rounded-full transition-colors ${isSaved ? 'text-[#015FA6] bg-[#E3F2FD]' : 'text-[#A5A5A5] hover:text-[#015FA6] hover:bg-[#F1F3F4]'}`}
                          title={isSaved ? 'Unsave job' : 'Save job'}
                        >
                          {isSaving ? <div className="w-4 h-4 border-2 border-[#015FA6] border-t-transparent rounded-full animate-spin" /> : <Bookmark size={15} fill={isSaved ? 'currentColor' : 'none'} />}
                        </button>
                        <button
                          onClick={(e) => hasApplied ? e.stopPropagation() : handleApply(job, e)}
                          disabled={isApplying}
                          className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors flex items-center gap-1.5 ${
                            hasApplied ? 'bg-[#E8F5E9] text-[#2E7D32] cursor-default'
                            : isApplying ? 'bg-[#014580] text-white cursor-wait'
                            : 'bg-[#015FA6] text-white hover:bg-[#014580]'
                          }`}
                        >
                          {isApplying ? (<><div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />Applying...</>)
                            : hasApplied ? (<><Check size={14} />Applied</>)
                            : 'Apply Now'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Pagination */}
            {renderPagination()}
          </>
        )}
        </div>
      </div>

      {/* Job Details Modal */}
      <Modal
        isOpen={isJobDetailsOpen}
        onClose={() => setIsJobDetailsOpen(false)}
        title={selectedJob?.title || 'Job Details'}
        maxWidth="max-w-2xl"
      >
        {selectedJob && (
          <div className="space-y-6">
            {/* Company Info */}
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-lg bg-[#015FA6] flex items-center justify-center text-white font-bold text-2xl">
                {(selectedJob.companyName || 'C').charAt(0)}
              </div>
              <div>
                <h3 className="text-xl font-semibold text-[#0B111E]">{selectedJob.title}</h3>
                <p className="text-base text-[#515151]">{selectedJob.companyName}</p>
              </div>
            </div>

            {/* Job Details Grid */}
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-3 p-3 bg-[#F9F9F9] rounded-lg">
                <MapPin size={20} className="text-[#015FA6]" />
                <div>
                  <p className="text-xs text-[#757575]">Location</p>
                  <p className="text-sm font-medium text-[#0B111E]">{formatLocation(selectedJob.location)}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-[#F9F9F9] rounded-lg">
                <DollarSign size={20} className="text-[#015FA6]" />
                <div>
                  <p className="text-xs text-[#757575]">Salary</p>
                  <p className="text-sm font-medium text-[#0B111E]">{formatSalary(selectedJob.salaryRange)}</p>
                </div>
              </div>
              {selectedJob.employmentType && (
                <div className="flex items-center gap-3 p-3 bg-[#F9F9F9] rounded-lg">
                  <Briefcase size={20} className="text-[#015FA6]" />
                  <div>
                    <p className="text-xs text-[#757575]">Employment Type</p>
                    <p className="text-sm font-medium text-[#0B111E]">{selectedJob.employmentType}</p>
                  </div>
                </div>
              )}
              {selectedJob.experienceLevel && (
                <div className="flex items-center gap-3 p-3 bg-[#F9F9F9] rounded-lg">
                  <Clock size={20} className="text-[#015FA6]" />
                  <div>
                    <p className="text-xs text-[#757575]">Experience Level</p>
                    <p className="text-sm font-medium text-[#0B111E]">{selectedJob.experienceLevel}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Description */}
            {selectedJob.description && (
              <div>
                <h4 className="text-sm font-semibold text-[#0B111E] mb-2">Job Description</h4>
                <p className="text-sm text-[#515151] whitespace-pre-line">{selectedJob.description}</p>
              </div>
            )}

            {/* Required Skills */}
            {selectedJob.requirements?.requiredSkills && selectedJob.requirements.requiredSkills.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-[#0B111E] mb-2">Required Skills</h4>
                <div className="flex flex-wrap gap-2">
                  {selectedJob.requirements.requiredSkills.map((skill, index) => (
                    <span
                      key={index}
                      className="px-3 py-1.5 bg-[#E3F2FD] text-[#015FA6] text-sm font-medium rounded-lg"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Preferred Skills */}
            {selectedJob.requirements?.preferredSkills && selectedJob.requirements.preferredSkills.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-[#0B111E] mb-2">Preferred Skills</h4>
                <div className="flex flex-wrap gap-2">
                  {selectedJob.requirements.preferredSkills.map((skill, index) => (
                    <span
                      key={index}
                      className="px-3 py-1.5 bg-[#F1F3F4] text-[#515151] text-sm font-medium rounded-lg"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Benefits */}
            {selectedJob.benefits && selectedJob.benefits.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-[#0B111E] mb-2">Benefits</h4>
                <ul className="list-disc list-inside space-y-1">
                  {selectedJob.benefits.map((benefit, index) => (
                    <li key={index} className="text-sm text-[#515151]">{benefit}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Action Buttons */}
            {(() => {
              const selectedJobId = getJobId(selectedJob);
              const hasApplied = appliedJobs.has(selectedJobId);
              const isSaved = savedJobs.has(selectedJobId);
              const isApplying = applyingJobId === selectedJobId;
              const isSaving = savingJobId === selectedJobId;

              return (
                <div className="flex gap-3">
                  {/* Save Button */}
                  <button
                    onClick={() => handleSaveJob(selectedJob)}
                    disabled={isSaving}
                    className={`px-4 py-3 rounded-lg font-medium text-base transition-colors flex items-center justify-center gap-2 border ${
                      isSaved
                        ? 'bg-[#E3F2FD] text-[#015FA6] border-[#015FA6]'
                        : 'bg-white text-[#515151] border-[#EDEDED] hover:border-[#015FA6] hover:text-[#015FA6]'
                    }`}
                  >
                    {isSaving ? (
                      <div className="w-4 h-4 border-2 border-[#015FA6] border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <Bookmark size={18} fill={isSaved ? 'currentColor' : 'none'} />
                    )}
                    {isSaved ? 'Saved' : 'Save'}
                  </button>

                  {/* Apply Button */}
                  <button
                    onClick={() => hasApplied ? null : handleApply(selectedJob)}
                    disabled={isApplying || hasApplied}
                    className={`flex-1 py-3 px-4 rounded-lg font-medium text-base transition-colors flex items-center justify-center gap-2 ${
                      hasApplied
                        ? 'bg-[#E8F5E9] text-[#2E7D32] cursor-default'
                        : isApplying
                        ? 'bg-[#014580] text-white cursor-wait'
                        : 'bg-[#015FA6] text-white hover:bg-[#014580]'
                    }`}
                  >
                    {isApplying ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Applying...
                      </>
                    ) : hasApplied ? (
                      <>
                        <Check size={18} />
                        Already Applied
                      </>
                    ) : (
                      'Apply Now'
                    )}
                  </button>
                </div>
              );
            })()}
          </div>
        )}
      </Modal>

      {/* Success/Error Modal */}
      <Modal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        showCloseButton={false}
        maxWidth="max-w-md"
      >
        <div className="text-center py-4">
          {isApplicationError ? (
            <>
              <div className="w-16 h-16 bg-[#FFEBEE] rounded-full flex items-center justify-center mx-auto mb-4">
                <X size={32} className="text-[#C62828]" />
              </div>
              <h2 className="text-xl font-semibold text-[#0B111E] mb-2">
                Application Failed
              </h2>
              <p className="text-sm text-[#757575] mb-6">
                {successJobTitle}
              </p>
              <button
                onClick={() => setShowSuccessModal(false)}
                className="px-6 py-2 bg-[#015FA6] text-white rounded-lg font-medium hover:bg-[#014580] transition-colors"
              >
                Try Again
              </button>
            </>
          ) : (
            <>
              <div className="w-16 h-16 bg-[#E8F5E9] rounded-full flex items-center justify-center mx-auto mb-4">
                <Check size={32} className="text-[#2E7D32]" />
              </div>
              <h2 className="text-xl font-semibold text-[#0B111E] mb-2">
                Application Submitted!
              </h2>
              <p className="text-sm text-[#757575] mb-6">
                You have successfully applied to <strong>{successJobTitle}</strong>.
                You can track your application status in the Applications page.
              </p>
              <div className="flex gap-3 justify-center">
                <button
                  onClick={() => setShowSuccessModal(false)}
                  className="px-6 py-2 border border-[#015FA6] text-[#015FA6] rounded-lg font-medium hover:bg-[#E3F2FD] transition-colors"
                >
                  Continue Browsing
                </button>
                <button
                  onClick={() => navigate('/candidate/applications')}
                  className="px-6 py-2 bg-[#015FA6] text-white rounded-lg font-medium hover:bg-[#014580] transition-colors"
                >
                  View Applications
                </button>
              </div>
            </>
          )}
        </div>
      </Modal>

      {/* Consent Modal */}
      <Modal
        isOpen={showConsentModal}
        onClose={() => {
          setShowConsentModal(false);
          setConsentJob(null);
          setConsentChecked(false);
        }}
        title="Apply for Position"
        maxWidth="max-w-md"
      >
        {consentJob && (
          <div className="space-y-6">
            <div className="text-center">
              <div className="w-16 h-16 rounded-lg bg-[#015FA6] flex items-center justify-center text-white font-bold text-2xl mx-auto mb-4">
                {(consentJob.companyName || 'C').charAt(0)}
              </div>
              <h3 className="text-lg font-semibold text-[#0B111E]">{consentJob.title}</h3>
              <p className="text-sm text-[#515151]">{consentJob.companyName}</p>
            </div>

            <div className="bg-[#F4F7FE] p-4 rounded-lg">
              <p className="text-sm text-[#515151] leading-relaxed">
                By clicking "Apply Now" you are giving David permission to share your profile with{' '}
                <strong className="text-[#0B111E]">{consentJob.companyName}</strong>.
              </p>
            </div>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={consentChecked}
                onChange={(e) => setConsentChecked(e.target.checked)}
                className="mt-1 w-5 h-5 rounded border-gray-300 text-[#015FA6] focus:ring-[#015FA6] cursor-pointer"
              />
              <span className="text-sm text-[#515151]">
                I agree to share my profile with {consentJob.companyName}
              </span>
            </label>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowConsentModal(false);
                  setConsentJob(null);
                  setConsentChecked(false);
                }}
                className="flex-1 px-4 py-3 border border-[#EDEDED] text-[#515151] rounded-lg font-medium hover:bg-[#F9F9F9] transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={submitApplication}
                disabled={!consentChecked}
                className={`flex-1 px-4 py-3 rounded-lg font-medium transition-colors ${
                  consentChecked
                    ? 'bg-[#015FA6] text-white hover:bg-[#014580]'
                    : 'bg-[#E5E5E5] text-[#A5A5A5] cursor-not-allowed'
                }`}
              >
                Apply Now
              </button>
            </div>
          </div>
        )}
      </Modal>
    </CandidateLayout>
  );
};

export default Jobs;
